/**
 * AI disclosure & provenance — the "Music Ingredients Label".
 *
 * Like a nutrition label on a loaf of bread, every track carries an honest
 * breakdown of how it was made: how much is AI-generated vs human-performed,
 * which model produced the AI parts, the influences/reference works the creator
 * fed in (with sources), and any live musicians overdubbed on top.
 *
 * Honesty rule: black-box models (Suno, Udio, …) do NOT expose their training
 * data, so we never claim to list what the MODEL was trained on. We capture the
 * CREATOR's declared inputs — the influences/references they actually used — and
 * label them as such.
 */

export type AILevel = 'human' | 'ai-assisted' | 'ai-generated';

export interface AIInfluence {
  /** A song or an artist the creator referenced / was influenced by. */
  type: 'song' | 'artist';
  name: string;
  /** Wikipedia-style source link (Wikipedia / MusicBrainz / official). */
  sourceUrl?: string;
}

export interface AILiveMusician {
  name: string;
  instrument?: string;
  /** Firestore user id, once the credit is linked to a real account. */
  userId?: string;
}

export interface AIDisclosure {
  /** Required: the headline involvement level. */
  level: AILevel;
  /** AI model/tool used, e.g. "Suno v4", "Udio", "MusicGen". */
  model?: string;
  /** Link to the model/tool. */
  modelUrl?: string;
  /** 0–100: share of the finished track that is AI-generated. */
  aiPercent?: number;
  /** 🎼 Publishing Side: % AI in Music Composition & Lyrics (0% = 100% Human Written, 100% = AI Written). */
  publishingAiPercent?: number;
  /** 🎙️ Recording Side: % AI in Master Recording & Audio Generation (0% = 100% Human Recorded, 100% = AI Generated Audio). */
  recordingAiPercent?: number;
  /** The prompt(s) the creator used. */
  prompt?: string;
  /** Creator-declared influences / reference works (with sources). */
  influences?: AIInfluence[];
  /** Live musicians overdubbed on top of the AI base. */
  liveMusicians?: AILiveMusician[];
  /** "Go full nerd" — the creator's making-of / process notes. */
  processNotes?: string;
  /** The RECORDING/master may be AI while the underlying SONG (composition +
   *  lyrics = the copyright) is 100% human-written. Set true for AI renderings
   *  of an existing human-written work. */
  compositionHuman?: boolean;
  /** The original human-written (and usually human-recorded) work this AI track
   *  recreates / is based on — e.g. "Song Title — Original Artist". */
  basedOn?: string;
  /** ISO timestamp the disclosure was completed. */
  disclosedAt?: string;
  /** AI-assisted plausibility read (from /api/ai/analyze-provenance). */
  analysis?: { summary?: string; confidence?: 'low' | 'medium' | 'high'; flags?: string[] };
}

export const AI_LEVELS: { value: AILevel; label: string; emoji: string; blurb: string; industryLabel?: string; industryText?: string }[] = [
  { value: 'human',        label: 'Human-made',    emoji: '🎙️', blurb: 'Performed and produced by people. No generative AI in the music.' },
  {
    value: 'ai-assisted',  label: 'AI-assisted',   emoji: '🤝', blurb: 'AI helped (e.g. stems, ideas), but humans wrote/performed the core.',
    industryLabel: 'AI-ASSISTED (track-level label)',
    industryText: 'Generative AI was used as a tool in the creation of the recording; the primary creative elements are human-made.',
  },
  {
    value: 'ai-generated', label: 'Contains AI',   emoji: '🤖', blurb: 'Significant parts were generated by an AI model.',
    // Recording-industry standard track-level label (verbatim).
    industryLabel: 'AI-GENERATED (track-level label)',
    industryText: 'Generative AI was used to generate the entirety or the primary portion of the creative elements of the recording.',
  },
];

export const AI_PERCENT_PRESETS: {
  percent: number;
  level: AILevel;
  compositionHuman?: boolean;
  label: string;
  shortLabel: string;
  emoji: string;
  description: string;
}[] = [
  {
    percent: 0,
    level: 'human',
    compositionHuman: true,
    label: '100% Human',
    shortLabel: '0% AI',
    emoji: '🎙️',
    description: '100% Human written and performed. No generative AI.',
  },
  {
    percent: 25,
    level: 'ai-assisted',
    compositionHuman: true,
    label: '25% AI (Assisted / Stems)',
    shortLabel: '25% AI',
    emoji: '🤝',
    description: 'Human music & recording with minor AI tools (stems, ideas, mixing).',
  },
  {
    percent: 50,
    level: 'ai-generated',
    compositionHuman: true,
    label: '50% AI (Human Song + AI Rec)',
    shortLabel: '50% AI',
    emoji: '⚖️',
    description: 'Music/Lyrics written by Human; Recording/Audio generated by AI.',
  },
  {
    percent: 75,
    level: 'ai-generated',
    compositionHuman: false,
    label: '75% AI (AI Music or Lyrics)',
    shortLabel: '75% AI',
    emoji: '🤖',
    description: 'Music or Lyrics generated by AI, with human production/touches.',
  },
  {
    percent: 100,
    level: 'ai-generated',
    compositionHuman: false,
    label: '100% AI (Fully Generated)',
    shortLabel: '100% AI',
    emoji: '⚡',
    description: '100% AI generated music, lyrics, and recording.',
  },
];

export function deriveAiPercent(d?: Partial<AIDisclosure>): number {
  if (!d || d.level === 'human') return 0;
  if (typeof d.publishingAiPercent === 'number' && typeof d.recordingAiPercent === 'number') {
    return Math.round((d.publishingAiPercent * 0.5) + (d.recordingAiPercent * 0.5));
  }
  if (typeof d.aiPercent === 'number') return d.aiPercent;
  if (d.compositionHuman) return 50;
  if (d.level === 'ai-assisted') return 25;
  return 75;
}

export function aiLevelMeta(level?: AILevel) {
  return AI_LEVELS.find(l => l.value === level) || AI_LEVELS[0];
}

/** A disclosure is "complete" enough to publish/distribute when: */
export function isDisclosureComplete(d?: AIDisclosure): boolean {
  if (!d || !d.level) return false;
  if (d.level === 'human') return true; // nothing more to declare
  // AI involved → must name the model and the % and disclose influences (or
  // explicitly state none) so the label is truthful.
  if (!d.model || typeof d.aiPercent !== 'number') return false;
  return true;
}

/** Why a disclosure is incomplete (for inline warnings). */
export function missingDisclosureReasons(d?: AIDisclosure): string[] {
  const out: string[] = [];
  if (!d || !d.level) { out.push('No AI involvement declared'); return out; }
  if (d.level === 'human') return out;
  if (!d.model) out.push('AI model/tool not named');
  if (typeof d.aiPercent !== 'number') out.push('AI percentage not set');
  if (!d.influences || d.influences.length === 0) out.push('No influences/sources credited');
  return out;
}

/** Distribution gate: AI tracks may not go to DSPs until disclosure is complete. */
export function canDistribute(d?: AIDisclosure): { ok: boolean; reason?: string } {
  if (!d || !d.level) return { ok: false, reason: 'AI disclosure required before distribution' };
  if (d.level === 'human') return { ok: true };
  if (!isDisclosureComplete(d)) return { ok: false, reason: 'Incomplete AI label — name the model + % before distributing' };
  return { ok: true };
}

/** Plain-English education shown when AI credits are missing. */
export const AI_CREDITS_EDUCATION =
  "Uncredited AI music erases the artists whose work trained or inspired it — " +
  "their style gets reused with no credit and no pay. Breakin' Bread labels every " +
  "track honestly so listeners know what they're hearing and the humans behind the " +
  "sound get named. Tell the story of how you made it.";
